/**
 * Fetch wrapper with automatic token refresh on 401
 */

import { DIRECTUS_INSTANCE } from '@/config/env';
import { getSecureToken, setSecureToken, removeSecureToken } from '@/utils/tokenEncryption';

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;
const rateLimitCooldownUntil = new Map<string, number>();

async function refreshTokenInternal(): Promise<boolean> {
  try {
    const token = getSecureToken('auth_token');
    const refreshTokenValue = getSecureToken('refresh_token');
    
    if (!token || !refreshTokenValue) {
      return false;
    }

    const response = await fetch(`${DIRECTUS_INSTANCE}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        refresh_token: refreshTokenValue
      })
    });

    const data = await response.json();

    if (!response.ok || !data.data?.access_token) {
      return false;
    }

    // Update tokens
    setSecureToken('auth_token', data.data.access_token);
    if (data.data.refresh_token) {
      setSecureToken('refresh_token', data.data.refresh_token);
    }

    return true;
  } catch (error) {
    console.error('Token refresh failed:', error);
    return false;
  }
}

/**
 * Sleep for a specified number of milliseconds
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Fetch with automatic token refresh on 401 and retry on 429 with exponential backoff
 * @param input - URL or Request
 * @param init - RequestInit options
 * @param retryOptions - Retry configuration
 * @returns Promise<Response>
 */
export async function fetchWithAuth(
  input: RequestInfo | URL,
  init?: RequestInit,
  retryOptions?: { maxRetries?: number; initialDelay?: number }
): Promise<Response> {
  const { maxRetries = 0, initialDelay = 1000 } = retryOptions || {};
  let retries = 0;
  let tokenRefreshAttempted = false; // Prevent infinite 401 loops
  
  const requestUrl = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
  let cooldownKey = requestUrl;
  try {
    const parsed = new URL(requestUrl, window.location.origin);
    // Cooldown by endpoint path (ignore query string noise).
    cooldownKey = `${parsed.origin}${parsed.pathname}`;
  } catch (_) {
    // Keep raw request URL as fallback.
  }

  const now = Date.now();
  const blockedUntil = rateLimitCooldownUntil.get(cooldownKey) || 0;
  if (blockedUntil > now) {
    const retryAfterSeconds = Math.max(1, Math.ceil((blockedUntil - now) / 1000));
    return new Response(
      JSON.stringify({
        success: false,
        error: 'Rate limit cooldown active',
        message: `Too many requests. Try again in ${retryAfterSeconds}s.`,
        retryAfter: retryAfterSeconds
      }),
      {
        status: 429,
        headers: { 'Content-Type': 'application/json' }
      }
    );
  }
  
  while (retries <= maxRetries) { // FIXED: Changed from while(true) to while(retries <= maxRetries)
    // First attempt (or retry)
    const response = await fetch(input, init);
    
    
    // Handle 401 (Unauthorized - token refresh)
    if (response.status === 401 && !tokenRefreshAttempted) {
      tokenRefreshAttempted = true; // Only try token refresh ONCE
      
      // Check if Authorization header is present
      const hasAuthHeader = init?.headers && 
        (init.headers instanceof Headers 
          ? init.headers.has('Authorization')
          : 'Authorization' in init.headers);
      
      // If no auth header, no point in refreshing
      if (!hasAuthHeader) {
        return response;
      }
      
      // Try to refresh token (only one refresh at a time)
      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = refreshTokenInternal().finally(() => {
          isRefreshing = false;
          refreshPromise = null;
        });
      }
      
      const refreshed = await refreshPromise;
      
      if (!refreshed) {
        // Refresh failed, sign out
        removeSecureToken('auth_token');
        removeSecureToken('refresh_token');
        localStorage.removeItem('guest_mode');
        window.dispatchEvent(new CustomEvent('auth:logout'));
        return response;
      }
      
      // Retry with new token
      const newToken = getSecureToken('auth_token');
      const newHeaders = new Headers(init?.headers);
      newHeaders.set('Authorization', `Bearer ${newToken}`);
      
      return fetch(input, {
        ...init,
        headers: newHeaders
      });
    }
    
    // Handle 429 (Too Many Requests - retry with exponential backoff)
    if (response.status === 429 && retries < maxRetries) {
      const delay = initialDelay * Math.pow(2, retries);
      
      // Try to get Retry-After header
      const retryAfterHeader = response.headers.get('Retry-After');
      const retryAfter = retryAfterHeader ? parseInt(retryAfterHeader) * 1000 : delay;
      rateLimitCooldownUntil.set(cooldownKey, Date.now() + Math.max(retryAfter, 2000));
      
      retries++;
      await sleep(retryAfter);
      
      // Continue to next iteration (retry)
      continue;
    }

    if (response.status === 429) {
      const retryAfterHeader = response.headers.get('Retry-After');
      const retryAfter = retryAfterHeader ? parseInt(retryAfterHeader) * 1000 : 5000;
      rateLimitCooldownUntil.set(cooldownKey, Date.now() + Math.max(retryAfter, 2000));
      return response;
    }
    
    // For server errors (5xx), retry with exponential backoff
    if (response.status >= 500 && response.status < 600 && retries < maxRetries) {
      const delay = initialDelay * Math.pow(2, retries);
      
      retries++;
      await sleep(delay);
      
      // Continue to next iteration (retry)
      continue;
    }
    
    // For all other responses (including successful, client errors, or exhausted retries), return as is
    return response;
  }
  
  // This should never be reached, but TypeScript requires it
  return fetch(input, init);
}

